import { Helmet } from "react-helmet-async";

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogImage?: string;
  type?: "website" | "article";
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
  breadcrumbs?: Array<{ name: string; url: string }>;
  noindex?: boolean;
}

const SEOHead = ({
  title = "Superdynamic - Personalización web con IA para ecommerce y SaaS",
  description = "Plataforma de personalización web con inteligencia artificial. Aumenta conversiones, reduce carritos abandonados y personaliza contenido sin código. Compatible con Shopify, WordPress, Webflow.",
  canonicalUrl = "https://superdynamic.io",
  ogImage = "https://superdynamic.io/og-image.png",
  type = "website",
  article,
  breadcrumbs,
  noindex = false,
}: SEOHeadProps) => {
  // Keywords optimizadas para búsquedas orgánicas en español
  const primaryKeywords = [
    "personalización web",
    "personalización ecommerce",
    "herramienta personalización web",
    "software personalización sitio web",
    "plataforma CRO",
    "optimización tasa conversión",
    "aumentar conversiones ecommerce",
    "reducir carritos abandonados",
    "popups personalizados",
    "exit intent popup",
    "A/B testing ecommerce",
    "segmentación visitantes web",
    "personalización con IA",
    "marketing automation ecommerce",
    "Shopify personalización",
    "WordPress popups",
    "Webflow personalización",
    "contenido dinámico web",
    "experiencia usuario personalizada",
    "CRO herramientas",
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Superdynamic",
    "applicationCategory": "BusinessApplication",
    "applicationSubCategory": "Marketing Automation Software",
    "operatingSystem": "Web",
    "description": "Plataforma de personalización web con IA que permite crear experiencias únicas para cada visitante. Aumenta conversiones con popups inteligentes, A/B testing automático y segmentación avanzada.",
    "url": "https://superdynamic.io",
    "offers": {
      "@type": "AggregateOffer",
      "lowPrice": "0",
      "highPrice": "299",
      "priceCurrency": "USD",
      "offerCount": "3",
      "offers": [
        {
          "@type": "Offer",
          "name": "Free",
          "price": "0",
          "priceCurrency": "USD",
          "description": "Plan gratuito para empezar. Hasta 1.000 visitantes/mes.",
          "availability": "https://schema.org/PreOrder"
        },
        {
          "@type": "Offer",
          "name": "Pro",
          "price": "99",
          "priceCurrency": "USD",
          "description": "Para negocios en crecimiento. Hasta 50.000 visitantes/mes.",
          "availability": "https://schema.org/PreOrder"
        },
        {
          "@type": "Offer",
          "name": "Enterprise",
          "price": "299",
          "priceCurrency": "USD",
          "description": "Para grandes empresas. Visitantes ilimitados y soporte dedicado.",
          "availability": "https://schema.org/PreOrder"
        }
      ]
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "89",
      "bestRating": "5",
      "worstRating": "1"
    },
    "featureList": [
      "Personalización web con inteligencia artificial",
      "Popups y banners personalizados por segmento",
      "Exit intent para reducir abandono de carritos",
      "A/B Testing automático con IA",
      "Segmentación avanzada de visitantes",
      "Analytics en tiempo real",
      "Integración con Shopify, WordPress, Webflow, Wix",
      "Sin necesidad de código",
      "Cumplimiento GDPR y privacidad",
      "Social proof y notificaciones dinámicas"
    ],
    "screenshot": ogImage,
    "softwareHelp": "https://superdynamic.io/recursos/documentacion",
    "releaseNotes": "https://superdynamic.io/recursos/blog"
  };

  const organizationData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Superdynamic",
    "url": "https://superdynamic.io",
    "logo": "https://superdynamic.io/logo.png",
    "description": "Superdynamic es una plataforma de personalización web con IA que ayuda a ecommerce y SaaS a aumentar conversiones mediante experiencias personalizadas.",
    "foundingDate": "2024",
    "sameAs": [
      "https://twitter.com/superdynamic_io",
      "https://linkedin.com/company/superdynamic",
      "https://github.com/superdynamic"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "availableLanguage": ["Spanish", "English"],
      "email": "hello@superdynamic.io"
    }
  };

  const faqData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "¿Qué es Superdynamic y cómo funciona la personalización web?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Superdynamic es una plataforma de personalización web con IA que analiza el comportamiento de cada visitante en tiempo real para mostrar contenido, ofertas y mensajes personalizados. Funciona añadiendo un simple script a tu web, sin necesidad de código ni desarrolladores."
        }
      },
      {
        "@type": "Question",
        "name": "¿Cuánto cuesta Superdynamic? ¿Hay plan gratuito?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Superdynamic ofrece un plan gratuito para empezar con hasta 1.000 visitantes/mes. Los planes de pago empiezan en $99/mes (Pro) para 50.000 visitantes y $299/mes (Enterprise) para visitantes ilimitados. Todos los planes incluyen todas las funcionalidades."
        }
      },
      {
        "@type": "Question",
        "name": "¿Con qué plataformas es compatible Superdynamic?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Superdynamic funciona con cualquier sitio web: Shopify, WooCommerce, WordPress, Webflow, Wix, Squarespace, Magento, PrestaShop y código personalizado. La integración es tan simple como añadir un script en tu web."
        }
      },
      {
        "@type": "Question",
        "name": "¿Superdynamic cumple con GDPR y la normativa de privacidad?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sí, Superdynamic está diseñado con privacidad primero. No requiere cookies de terceros, respeta las preferencias de consentimiento y cumple con GDPR, CCPA y otras normativas de privacidad. Los datos se procesan en la UE."
        }
      },
      {
        "@type": "Question",
        "name": "¿Cómo ayuda Superdynamic a aumentar conversiones?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Superdynamic aumenta conversiones mediante: popups personalizados que aparecen en el momento óptimo, exit intent para recuperar visitantes que abandonan, A/B testing automático con IA que encuentra las mejores variantes, y segmentación que muestra el contenido más relevante a cada usuario."
        }
      },
      {
        "@type": "Question",
        "name": "¿Necesito saber programar para usar Superdynamic?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Superdynamic está diseñado para equipos de marketing sin conocimientos técnicos. Puedes crear campañas, popups y personalizar contenido con un editor visual intuitivo. Si quieres, también puedes usar código personalizado para casos avanzados."
        }
      }
    ]
  };

  const breadcrumbData = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  } : null;

  const articleData = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": ogImage,
    "datePublished": article.publishedTime,
    "dateModified": article.modifiedTime || article.publishedTime,
    "author": {
      "@type": "Person",
      "name": article.author || "Equipo Superdynamic"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Superdynamic",
      "logo": {
        "@type": "ImageObject",
        "url": "https://superdynamic.io/logo.png"
      }
    },
    "articleSection": article.section,
    "keywords": article.tags?.join(", "),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    }
  } : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={primaryKeywords.join(", ")} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
      <meta name="language" content="es" />
      <meta name="author" content="Superdynamic" />
      <meta name="generator" content="Superdynamic" />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Alternate languages */}
      <link rel="alternate" hrefLang="es" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type === "article" ? "article" : "website"} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="es_ES" />
      <meta property="og:site_name" content="Superdynamic" />
      
      {/* Article specific OG tags */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          {article.modifiedTime && <meta property="article:modified_time" content={article.modifiedTime} />}
          <meta property="article:author" content={article.author || "Equipo Superdynamic"} />
          {article.section && <meta property="article:section" content={article.section} />}
          {article.tags?.map((tag, i) => (
            <meta key={i} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@superdynamic_io" />
      <meta name="twitter:creator" content="@superdynamic_io" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(organizationData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqData)}
      </script>
      {breadcrumbData && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbData)}
        </script>
      )}
      {articleData && (
        <script type="application/ld+json">
          {JSON.stringify(articleData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;